import { QueryClient, UseMutationOptions, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { Item, Room, SnapshotImage, Video } from 'src/types';
import { createScopedMutation } from '../createScopedMutation';
import {
  JOB_QUERY_KEY,
  DELETE_KEY,
  ENTITY_KEY,
  IMAGE_QUERY_KEY,
  ITEM_QUERY_KEY,
  LIST_KEY,
  POST_KEY,
  PUT_KEY,
  ROOM_QUERY_KEY,
  VIDEO_QUERY_KEY,
} from '../queryKeys';
import {
  invalidateIfAllMutationsDone,
  JSON_MUTATION_TIMEOUT_MS,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
} from '../queryYemboApiCalls';
import { useIsReadyForFetch } from '../useIsReadyForFetch';

const url = 'room';

/**
 * A job's rooms, newest first: `GET /room?jobKey=` answers `{ rooms }`, an
 * empty list when there are none, and 404 for a job that does not exist.
 */
const getRooms = async (jobKey: string): Promise<Room[]> => {
  const rooms = await queryYemboApiCallWithReturnValue<Room[], 'rooms'>({
    url,
    key: 'rooms',
    urlParams: new URLSearchParams({ jobKey }),
    shouldNotifyAndThrowError: true,
  });

  if (!rooms) throw new Error('Job not found');

  return rooms;
};

const postRoom = (room: Room) =>
  queryYemboApiCall({
    url,
    method: 'POST',
    body: JSON.stringify(room),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

const putRoom = (room: Partial<Room> & Pick<Room, 'key'>) =>
  queryYemboApiCall({
    url,
    method: 'PUT',
    body: JSON.stringify(room),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

const deleteRoom = (key: string) =>
  queryYemboApiCall({
    url,
    method: 'DELETE',
    body: JSON.stringify({ key }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

export const initializeRooms = (data: { jobKey: string; beds?: number | null }) =>
  queryYemboApiCall({
    url: `${url}/initialize`,
    method: 'POST',
    body: JSON.stringify(data),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

type UseRoomsOptions = Partial<UseQueryOptions<Room[], unknown>>;

const useRoomsQuery = (jobKey: string | undefined, options?: UseRoomsOptions) =>
  useQuery({
    queryKey: [ROOM_QUERY_KEY, LIST_KEY, jobKey],
    queryFn: () => getRooms(jobKey ?? ''),
    ...options,
    enabled: !!jobKey && options?.enabled !== false,
  });

export const useRoomsQueryForOffline = (jobKey: string | undefined, options?: UseRoomsOptions) =>
  useRoomsQuery(jobKey, {
    ...options,
    enabled: useIsReadyForFetch() && options?.enabled !== false,
  });

type PostRoomOptions = UseMutationOptions<Response, unknown, Room, void>;

const getPostRoomMutationOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: PostRoomOptions
): PostRoomOptions => {
  const listQueryKey = [ROOM_QUERY_KEY, LIST_KEY, jobKey];

  return {
    mutationKey: [ROOM_QUERY_KEY, jobKey, POST_KEY],
    scope: { id: roomKey },
    mutationFn: postRoom,
    ...options,
    onMutate: async (room, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<Room[]>(listQueryKey, (prevRooms) => (prevRooms ? [...prevRooms, room] : undefined));

      options?.onMutate?.(room, context);
    },
    onError: async (error, room, onMutateResult, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<Room[]>(listQueryKey, (prevRooms) => prevRooms?.filter(({ key }) => key !== room.key));

      options?.onError?.(error, room, onMutateResult, context);
    },
    onSettled: (response, error, room, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [ROOM_QUERY_KEY, jobKey]);
      options?.onSettled?.(response, error, room, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: usePostRoom, useScopedMutationBuilder: usePostRoomBuilder } =
  createScopedMutation(getPostRoomMutationOptions);

type DeleteRoomOptions = UseMutationOptions<Response, unknown, Pick<Room, 'key' | 'jobKey'>, void>;

const getDeleteRoomOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: DeleteRoomOptions
): DeleteRoomOptions => {
  const mutationKey = [ROOM_QUERY_KEY, jobKey, DELETE_KEY];

  return {
    mutationKey,
    scope: { id: roomKey },
    mutationFn: (room) => deleteRoom(room.key),
    ...options,
    onMutate: async (room, context) => {
      const listQueryKey = [ROOM_QUERY_KEY, LIST_KEY, room.jobKey];
      const videoListQueryKey = [VIDEO_QUERY_KEY, LIST_KEY, room.jobKey];
      const imageListQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, room.jobKey];
      const tagListQueryKey = [ITEM_QUERY_KEY, LIST_KEY, room.jobKey];

      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({ queryKey: videoListQueryKey }),
        queryClient.cancelQueries({ queryKey: imageListQueryKey }),
        queryClient.cancelQueries({ queryKey: tagListQueryKey }),
      ]);

      // Remove room
      queryClient.setQueryData<Room[]>(listQueryKey, (prevRooms) => prevRooms?.filter(({ key }) => key !== room.key));

      // Remove room videos
      queryClient.setQueryData<Video[]>(videoListQueryKey, (prevVideos) => {
        if (!prevVideos) return;

        const videosToPreserve: Video[] = [];
        const videosToRemove: Video[] = [];

        for (const video of prevVideos) {
          if (video.roomKey === room.key) {
            videosToRemove.push(video);
          } else {
            videosToPreserve.push(video);
          }
        }

        // Remove room images
        queryClient.setQueryData<SnapshotImage[]>(imageListQueryKey, (prevImages) => {
          if (!prevImages) return;

          const imagesToPreserve: SnapshotImage[] = [];
          const imagesToRemove: SnapshotImage[] = [];

          for (const image of prevImages) {
            if (videosToRemove.some(({ key }) => key === image.videoKey)) {
              imagesToRemove.push(image);
            } else {
              imagesToPreserve.push(image);
            }
          }

          // Remove room items
          queryClient.setQueryData<Item[]>(tagListQueryKey, (prevTags) =>
            prevTags?.filter(({ imageKey }) => !imagesToRemove.some(({ key }) => key === imageKey))
          );

          return imagesToPreserve;
        });

        return videosToPreserve;
      });

      options?.onMutate?.(room, context);
    },
    onSettled: (response, error, room, onMutateResult, context) => {
      invalidateIfAllMutationsDone([ROOM_QUERY_KEY, LIST_KEY, room.jobKey], [ROOM_QUERY_KEY, jobKey]);
      invalidateIfAllMutationsDone([JOB_QUERY_KEY, ENTITY_KEY, room.jobKey], mutationKey);
      invalidateIfAllMutationsDone([VIDEO_QUERY_KEY, LIST_KEY, room.jobKey], mutationKey);
      invalidateIfAllMutationsDone([IMAGE_QUERY_KEY, LIST_KEY, room.jobKey], mutationKey);
      invalidateIfAllMutationsDone([ITEM_QUERY_KEY, LIST_KEY, room.jobKey], mutationKey);

      options?.onSettled?.(response, error, room, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: useDeleteRoom, useScopedMutationBuilder: useDeleteRoomBuilder } =
  createScopedMutation(getDeleteRoomOptions);

type PartialRoom = Partial<Room> & Pick<Room, 'key'>;
type PutRoomOptions = UseMutationOptions<Response, unknown, PartialRoom>;

const getPutRoomMutationOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: PutRoomOptions
): PutRoomOptions => {
  return {
    mutationKey: [ROOM_QUERY_KEY, jobKey, PUT_KEY],
    scope: { id: roomKey },
    mutationFn: putRoom,
    ...options,
    onMutate: async (room, context) => {
      const listQueryKey = [ROOM_QUERY_KEY, LIST_KEY, jobKey];

      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<Room[]>(listQueryKey, (prevRooms) =>
        prevRooms?.map((prevRoom) => (room.key === prevRoom.key ? { ...prevRoom, ...room } : prevRoom))
      );

      options?.onMutate?.(room, context);
    },
    onSettled: (response, error, room, onMutateResult, context) => {
      invalidateIfAllMutationsDone([ROOM_QUERY_KEY, LIST_KEY, jobKey], [ROOM_QUERY_KEY, jobKey]);
      options?.onSettled?.(response, error, room, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: usePutRoom, useScopedMutationBuilder: usePutRoomBuilder } =
  createScopedMutation(getPutRoomMutationOptions);
